import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Camera, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, photoUrl, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { useApiErrorHandler } from "@/contexts/PaywallContext";

const AMENITIES = [
    "wifi", "furnished", "washer", "dryer", "dishwasher", "balcony", "pet_friendly",
    "parking", "gym", "elevator", "ac", "heating", "roof_access", "utilities_included",
];
const GENDERS = ["Any", "Woman", "Man", "Non-binary"];

export default function PostListingSheet({ open, onClose, onCreated }) {
    const handleApiError = useApiErrorHandler();
    const [title, setTitle] = useState("");
    const [city, setCity] = useState("");
    const [rent, setRent] = useState("");
    const [moveIn, setMoveIn] = useState("");
    const [gender, setGender] = useState("Any");
    const [description, setDescription] = useState("");
    const [amenities, setAmenities] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const fileRef = useRef(null);

    const reset = () => {
        setTitle(""); setCity(""); setRent(""); setMoveIn(""); setGender("Any");
        setDescription(""); setAmenities([]); setPhotos([]);
    };

    const uploadPhotos = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                setPhotos((p) => [...p, data.id]);
            }
        } catch (err) {
            toast.error(formatDetail(err));
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!title || !city || !rent || !moveIn) {
            toast.error("Fill title, city, rent and move-in date");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/listings", {
                title, city, rent: Number(rent),
                move_in_date: moveIn,
                gender_preference: gender,
                description, amenities, photos,
            });
            toast.success("Listing posted!");
            reset();
            onCreated?.(data);
            onClose?.();
        } catch (err) {
            if (!handleApiError(err)) {
                toast.error(formatDetail(err));
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[70] bg-background/70 backdrop-blur-xl"
                    onClick={onClose}
                    data-testid="post-listing-sheet"
                >
                    <motion.form
                        initial={{ y: "100%" }}
                        animate={{ y: 0 }}
                        exit={{ y: "100%" }}
                        transition={{ type: "spring", stiffness: 260, damping: 30 }}
                        onSubmit={submit}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute inset-x-0 bottom-0 mx-auto max-w-xl rounded-t-4xl border-t border-border bg-card p-6 pb-8 max-h-[92vh] overflow-y-auto no-scrollbar"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="font-display text-2xl font-black">Post your flat</div>
                                <div className="text-xs text-muted-foreground">Reach GenZ roommates instantly</div>
                            </div>
                            <button type="button" onClick={onClose} className="h-9 w-9 rounded-full hover:bg-accent grid place-items-center" data-testid="post-listing-close">
                                <X className="h-5 w-5" strokeWidth={2.5} />
                            </button>
                        </div>

                        {/* Photos */}
                        <div>
                            <Label className="text-sm font-semibold">Photos</Label>
                            <div className="mt-2 grid grid-cols-3 gap-2">
                                {photos.map((id, i) => (
                                    <div key={id} className="relative aspect-square rounded-2xl overflow-hidden border border-border group">
                                        <img src={photoUrl(id)} alt="photo" className="h-full w-full object-cover" />
                                        <button
                                            type="button"
                                            onClick={() => setPhotos((p) => p.filter((x) => x !== id))}
                                            className="absolute top-1.5 right-1.5 bg-black/60 text-white rounded-full p-1"
                                            data-testid={`listing-photo-remove-${i}`}
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </div>
                                ))}
                                {photos.length < 6 && (
                                    <button
                                        type="button"
                                        onClick={() => fileRef.current?.click()}
                                        data-testid="listing-photo-add"
                                        className="aspect-square rounded-2xl border-2 border-dashed border-border grid place-items-center hover:border-primary hover:bg-accent lift"
                                    >
                                        <div className="text-center text-muted-foreground">
                                            <Camera className="mx-auto h-5 w-5" strokeWidth={2.5} />
                                            <div className="text-[10px] mt-1">{uploading ? "…" : "Add"}</div>
                                        </div>
                                    </button>
                                )}
                                <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={uploadPhotos} data-testid="listing-photo-input" />
                            </div>
                        </div>

                        <div className="mt-4 space-y-3">
                            <div>
                                <Label htmlFor="lt-title" className="text-sm font-semibold">Title</Label>
                                <Input id="lt-title" value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="Bright 2BR in Kreuzberg" className="mt-1 h-12 rounded-2xl" data-testid="listing-title-input" />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label htmlFor="lt-city" className="text-sm font-semibold">City</Label>
                                    <Input id="lt-city" value={city} onChange={(e) => setCity(e.target.value)} required placeholder="Bengaluru" className="mt-1 h-12 rounded-2xl" data-testid="listing-city-input" />
                                </div>
                                <div>
                                    <Label htmlFor="lt-rent" className="text-sm font-semibold">Rent /mo (₹)</Label>
                                    <Input id="lt-rent" type="number" min={0} value={rent} onChange={(e) => setRent(e.target.value)} required placeholder="18000" className="mt-1 h-12 rounded-2xl" data-testid="listing-rent-input" />
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="lt-move" className="text-sm font-semibold">Move-in date</Label>
                                <Input id="lt-move" type="date" value={moveIn} onChange={(e) => setMoveIn(e.target.value)} required className="mt-1 h-12 rounded-2xl" data-testid="listing-movein-input" />
                            </div>
                            <div>
                                <Label className="text-sm font-semibold">Gender preference</Label>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {GENDERS.map((g) => (
                                        <button
                                            key={g}
                                            type="button"
                                            onClick={() => setGender(g)}
                                            data-testid={`listing-gender-${g.toLowerCase().replace(/[^a-z]/g, "-")}`}
                                            className={`rounded-full px-4 py-2 text-sm font-medium lift ${
                                                gender === g ? "bg-primary text-primary-foreground" : "border border-border bg-background hover:bg-accent"
                                            }`}
                                        >
                                            {g}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <Label className="text-sm font-semibold">Amenities</Label>
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {AMENITIES.map((a) => {
                                        const active = amenities.includes(a);
                                        return (
                                            <button
                                                key={a}
                                                type="button"
                                                onClick={() => setAmenities(active ? amenities.filter((x) => x !== a) : [...amenities, a])}
                                                data-testid={`listing-amenity-${a}`}
                                                className={`rounded-full px-3 py-1.5 text-xs font-semibold lift ${
                                                    active ? "bg-primary text-primary-foreground" : "border border-border bg-background hover:bg-accent"
                                                }`}
                                            >
                                                {a.replace(/_/g, " ")}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="lt-desc" className="text-sm font-semibold">Description</Label>
                                <Textarea id="lt-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Sunlight, plants, the works. Tell them what's special." rows={4} className="mt-1 rounded-2xl" data-testid="listing-description-input" />
                            </div>
                        </div>

                        <Button type="submit" disabled={saving} data-testid="listing-submit-btn" className="mt-6 h-14 w-full rounded-2xl font-semibold text-base lift">
                            {saving ? "Posting…" : (<><Plus className="mr-2 h-5 w-5" /> Post listing</>)}
                        </Button>
                    </motion.form>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
